import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../../utils/creatorLang';
import {
  CreatorScreen, Card, Pill, BottomNav, ScreenHeader,
  I, SectionLabel,
} from '../../components/creator/primitives';

const CreatorGuidelines = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const debugParam = searchParams.get('creator');
  const qs = debugParam ? `?creator=${debugParam}` : '';

  const [expand, setExpand] = useState(0);

  const sections = [
    {
      title: t('gl_youth_safety'), icon: 'Shield', tint: 'green',
      items: [t('gl_youth_1'), t('gl_youth_2'), t('gl_youth_3')],
    },
    {
      title: t('gl_safety'), icon: 'Alert', tint: 'red',
      items: [t('gl_safety_1'), t('gl_safety_2'), t('gl_safety_3'), t('gl_safety_4')],
    },
    {
      title: t('gl_integrity'), icon: 'Check', tint: 'gold',
      items: [t('gl_integrity_1'), t('gl_integrity_2'), t('gl_integrity_3')],
    },
    {
      title: t('gl_mental'), icon: 'Sparkle', tint: 'cyan',
      items: [t('gl_mental_1'), t('gl_mental_2')],
    },
  ];

  return (
    <CreatorScreen>
      <div className="screen grain">
        <ScreenHeader
          title={t('guidelines_title')}
          subtitle="TikTok Community"
          onBack={() => navigate(`/my/dashboard${qs}`)}
        />

        {/* Intro */}
        <div style={{ padding: '0 20px 12px' }}>
          <Card pad={16}>
            <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}>
              {t('guidelines_intro')}
            </div>
          </Card>
        </div>

        {/* Community principles */}
        <div style={{ padding: '0 20px 12px' }}>
          <SectionLabel>{t('gl_principles')}</SectionLabel>
          <Card pad={14}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {['Prevent Harm', 'Support Free Expression', 'Encourage Kindness', 'Respect Local Cultures', 'Champion Inclusion', 'Protect Privacy', 'Be Transparent', 'Act Fairly'].map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px',
                  background: 'var(--accent-cyan-soft)',
                  border: '1px solid oklch(0.78 0.16 210 / 0.15)',
                  borderRadius: 10,
                }}>
                  <I.Sparkle size={12} stroke="var(--accent-cyan)"/>
                  <span style={{ fontSize: 11, color: 'var(--fg-1)', fontWeight: 500 }}>{p}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Accordion sections */}
        <div style={{ padding: '0 20px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sections.map((s, i) => {
            const IconC = I[s.icon];
            const open = expand === i;
            return (
              <div key={i} style={{
                background: 'var(--bg-2)',
                border: '1px solid var(--line-1)',
                borderRadius: 16, overflow: 'hidden',
              }}>
                <div className="tap" onClick={() => setExpand(open ? -1 : i)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 16,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `var(--accent-${s.tint}-soft)`,
                    border: `1px solid oklch(from var(--accent-${s.tint}) l c h / 0.25)`,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <IconC size={16} stroke={`var(--accent-${s.tint})`}/>
                  </div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--fg-0)' }}>{s.title}</div>
                  <div style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    <I.ChevronDown size={16} stroke="var(--fg-3)"/>
                  </div>
                </div>
                {open && (
                  <div style={{ padding: '0 16px 16px 62px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {s.items.map((it, j) => (
                      <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: `var(--accent-${s.tint})`, marginTop: 7, flexShrink: 0 }}/>
                        <span style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.55 }}>{it}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Enforcement */}
        <div style={{ padding: '0 20px 12px' }}>
          <SectionLabel>{t('gl_enforcement')}</SectionLabel>
          <Card pad={16}>
            {[
              { text: t('gl_enforce_1'), color: 'var(--accent-red)' },
              { text: t('gl_enforce_2'), color: 'var(--accent-gold)' },
              { text: t('gl_enforce_3'), color: 'oklch(0.82 0.18 95)' },
            ].map((row, i, arr) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                paddingTop: i === 0 ? 0 : 10,
                paddingBottom: i < arr.length - 1 ? 10 : 0,
                borderBottom: i < arr.length - 1 ? '1px solid var(--line-1)' : 'none',
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: row.color, flexShrink: 0 }}/>
                <span style={{ fontSize: 12, color: 'var(--fg-1)' }}>{row.text}</span>
              </div>
            ))}
          </Card>
        </div>

        {/* External link */}
        <div style={{ padding: '0 20px' }}>
          <a
            href="https://www.tiktok.com/safety/en/policies-and-engagement/overview"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', display: 'block' }}
          >
            <Card pad={14} style={{
              background: 'var(--accent-pink-soft)',
              border: '1px solid oklch(0.68 0.22 10 / 0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}>
              <I.Link size={16} stroke="var(--accent-pink)"/>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-pink)' }}>
                {t('gl_full_link')}
              </span>
            </Card>
          </a>
        </div>
      </div>

      <BottomNav active="" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorGuidelines;
